import type { ComponentType, ReactNode, SVGProps } from 'react';
import { Link, NavLink } from 'react-router';
import { useConsulta } from '../dados/contexto.js';
import { trocarModo } from '../lib/modo.js';
import { lerPerfil, trocarPerfil, type Perfil } from '../lib/perfil.js';
import { supabase } from '../lib/supabase.js';
import { useTema, type PreferenciaTema } from '../lib/tema.js';
import type { PublicoVisao } from '../lib/visoes.js';
import { IconeEquipe, IconeHaras, IconeLua, IconeMonitor, IconeSair, IconeSol } from './Icones.js';
import { Marca } from './Marca.js';
import { IndicadorSincronia } from './Sincronia.js';
import { Interruptor } from './ui/index.js';
import { VisoesDaLateral } from './VisoesRapidas.js';

export interface ItemNavegacao {
  para: string;
  rotulo: string;
  Icone: ComponentType<SVGProps<SVGSVGElement>>;
  /** So ativo no caminho exato (Inicio, em '/'). */
  fim?: boolean;
}

/**
 * Barra lateral do desktop (a partir de `lg`). Abismo, como era o topo: marca,
 * o haras em uso, a navegacao, as visoes rapidas do publico, a administracao e,
 * no pe, o que e do aparelho (demonstracao, sincronia, tema, sessao).
 *
 * Abaixo de `lg` ela nao existe: o celular continua com topo e barra de baixo,
 * que o polegar alcanca no campo (ver Layout).
 */
export function BarraLateral({
  navegacao,
  publico,
  linkHaras,
  admin,
  demonstracao,
  interruptorDemo,
  sessao,
  persistente,
}: {
  navegacao: readonly ItemNavegacao[];
  publico: PublicoVisao;
  /** Rotulo do link para os haras (equipe ve todos; o dono, os seus), ou null. */
  linkHaras: string | null;
  admin: boolean;
  /** Bloco "Ver como" + dados ficticios (sem login ou com dados ficticios). */
  demonstracao: { ficticia: boolean; aviso: string | null } | null;
  /** Com login e dados reais, so o interruptor de dados ficticios. */
  interruptorDemo: { ligado: boolean } | null;
  sessao: { email: string | undefined } | null;
  persistente: boolean;
}) {
  return (
    <aside
      aria-label="Barra lateral"
      className="sticky top-0 hidden h-dvh flex-col overflow-y-auto bg-lateral px-3.5 pt-5 pb-4 text-sobre-lateral lg:flex print:hidden"
    >
      <NavLink to="/" aria-label="Equiview, início" className="mx-2 mb-5 self-start rounded-(--radius-controle)">
        <Marca tom="escuro" className="h-7" />
      </NavLink>

      <HarasAtual podeTrocar={!!linkHaras} />

      <Secao titulo="Painel">
        <nav aria-label="Principal" className="flex flex-col gap-0.5">
          {navegacao.map(({ para, rotulo, Icone, fim }) => (
            <NavLink
              key={para}
              to={para}
              end={!!fim}
              className={({ isActive }) =>
                `flex min-h-10 items-center gap-3 rounded-(--radius-controle) px-3 text-[13.5px] font-medium transition-colors ${
                  isActive
                    ? 'bg-nevoa font-semibold text-abismo'
                    : 'text-sobre-lateral/80 hover:bg-lateral-hover hover:text-sobre-lateral'
                }`
              }
            >
              <Icone width={18} height={18} className="shrink-0" />
              {rotulo}
            </NavLink>
          ))}
        </nav>
      </Secao>

      <Secao titulo="Visões rápidas">
        <VisoesDaLateral publico={publico} />
      </Secao>

      {(linkHaras || admin) && (
        <Secao titulo="Administração">
          <nav aria-label="Administração" className="flex flex-col gap-0.5">
            {linkHaras && (
              <ItemSimples para="/haras" Icone={IconeHaras}>
                {linkHaras}
              </ItemSimples>
            )}
            {admin && (
              <ItemSimples para="/equipe" Icone={IconeEquipe}>
                Equipe e acessos
              </ItemSimples>
            )}
          </nav>
        </Secao>
      )}

      <div className="mt-auto space-y-3 pt-6">
        {demonstracao && <Demonstracao {...demonstracao} />}
        {interruptorDemo && (
          <div className="px-3">
            <Interruptor naLateral ligado={interruptorDemo.ligado} onMudar={(l) => trocarModo(l ? 'demo' : 'real')}>
              Dados fictícios
            </Interruptor>
          </div>
        )}
        {sessao && <IndicadorSincronia naLateral persistente={persistente} />}
        <SeletorTemaLateral />
        {sessao && (
          <div className="flex items-center gap-2 border-t border-gelo/10 px-1 pt-3">
            <p className="min-w-0 flex-1 truncate px-2 text-xs text-lateral-apagado" title={sessao.email}>
              {sessao.email}
            </p>
            <button
              type="button"
              onClick={() => void supabase.auth.signOut()}
              title="Sair"
              aria-label="Sair"
              className="flex size-9 items-center justify-center rounded-(--radius-controle) text-lateral-apagado hover:bg-lateral-hover hover:text-sobre-lateral"
            >
              <IconeSair width={18} height={18} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="mt-1">
      <h2 className="px-3 pt-4 pb-2 text-[10.5px] font-semibold tracking-[0.14em] text-lateral-apagado uppercase">
        {titulo}
      </h2>
      {children}
    </section>
  );
}

function ItemSimples({
  para,
  Icone,
  children,
}: {
  para: string;
  Icone: ComponentType<SVGProps<SVGSVGElement>>;
  children: ReactNode;
}) {
  return (
    <NavLink
      to={para}
      className={({ isActive }) =>
        `flex min-h-10 items-center gap-3 rounded-(--radius-controle) px-3 text-[13.5px] font-medium transition-colors ${
          isActive
            ? 'bg-lateral-ativo text-sobre-lateral shadow-[inset_3px_0_0_var(--color-nevoa)]'
            : 'text-sobre-lateral/80 hover:bg-lateral-hover hover:text-sobre-lateral'
        }`
      }
    >
      <Icone width={18} height={18} className="shrink-0" />
      <span className="truncate">{children}</span>
    </NavLink>
  );
}

/** O haras em uso: todo numero da tela e dele. Quem cadastra pode trocar. */
function HarasAtual({ podeTrocar }: { podeTrocar: boolean }) {
  const haras = useConsulta((f) => f.haras(), []);
  if (haras.estado !== 'pronto' || !haras.dado) return null;
  const h = haras.dado;
  return (
    <div className="mx-1 rounded-(--radius-controle) bg-lateral-hover px-3 py-2.5">
      <p className="text-[10.5px] font-semibold tracking-[0.14em] text-lateral-apagado uppercase">Haras</p>
      <p className="truncate text-sm font-semibold" title={h.name}>
        {h.name}
      </p>
      <p className="flex items-baseline justify-between gap-2 text-xs text-lateral-apagado">
        <span className="truncate">{h.city ? `${h.city}/${h.uf}` : 'Sem cidade'}</span>
        {podeTrocar && (
          <Link to="/haras" className="alvo-toque shrink-0 font-medium text-nevoa hover:underline">
            Trocar
          </Link>
        )}
      </p>
    </div>
  );
}

const PERFIS: ReadonlyArray<{ valor: Perfil; rotulo: string; titulo: string }> = [
  {
    valor: 'avaliador',
    rotulo: 'Avaliador',
    titulo: 'Ver como avaliador da equipe Equiview',
  },
  { valor: 'haras', rotulo: 'Dono', titulo: 'Ver como dono do haras' },
];

/** O mesmo da FaixaDeContexto do celular, nas cores da barra lateral. */
function Demonstracao({ ficticia, aviso }: { ficticia: boolean; aviso: string | null }) {
  const perfil = lerPerfil();
  return (
    <div className="space-y-2 rounded-(--radius-controle) border border-gelo/10 p-2.5">
      <p className="px-0.5 text-[10.5px] font-semibold tracking-[0.14em] text-lateral-apagado uppercase">Ver como</p>
      <Segmentado
        rotulo="Ver como"
        valor={perfil}
        onMudar={trocarPerfil}
        opcoes={PERFIS.map((p) => ({ ...p, conteudo: p.rotulo }))}
      />
      <div className="px-0.5">
        <Interruptor naLateral ligado={ficticia} onMudar={(l) => trocarModo(l ? 'demo' : 'real')}>
          Dados fictícios
        </Interruptor>
      </div>
      {aviso && <p className="px-0.5 text-xs leading-snug text-lateral-apagado">{aviso}</p>}
    </div>
  );
}

const TEMAS: ReadonlyArray<{
  valor: PreferenciaTema;
  titulo: string;
  conteudo: ReactNode;
}> = [
  {
    valor: 'claro',
    titulo: 'Tema claro',
    conteudo: (
      <>
        <IconeSol width={16} height={16} />
        Claro
      </>
    ),
  },
  {
    valor: 'escuro',
    titulo: 'Tema escuro',
    conteudo: (
      <>
        <IconeLua width={16} height={16} />
        Escuro
      </>
    ),
  },
  {
    valor: 'sistema',
    titulo: 'Igual ao sistema',
    conteudo: (
      <>
        <IconeMonitor width={16} height={16} />
        Auto
      </>
    ),
  },
];

/** Tema a um clique (no topo do celular ele fica num menu). */
function SeletorTemaLateral() {
  const { preferencia, escolher } = useTema();
  return <Segmentado rotulo="Tema" valor={preferencia} onMudar={escolher} opcoes={TEMAS} />;
}

/** Controle segmentado nas cores da barra lateral (o Alternador e para superficie clara). */
function Segmentado<T extends string>({
  rotulo,
  valor,
  onMudar,
  opcoes,
}: {
  rotulo: string;
  valor: T;
  onMudar: (v: T) => void;
  opcoes: ReadonlyArray<{ valor: T; titulo: string; conteudo: ReactNode }>;
}) {
  return (
    <div role="radiogroup" aria-label={rotulo} className="flex gap-1 rounded-(--radius-controle) bg-gelo/7 p-1">
      {opcoes.map((o) => {
        const ativo = o.valor === valor;
        return (
          <button
            key={o.valor}
            type="button"
            role="radio"
            aria-checked={ativo}
            title={o.titulo}
            onClick={() => onMudar(o.valor)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-[6px] px-1.5 text-xs font-semibold transition-colors ${
              ativo ? 'bg-nevoa text-abismo' : 'text-lateral-apagado hover:text-sobre-lateral'
            }`}
          >
            {o.conteudo}
          </button>
        );
      })}
    </div>
  );
}
